import React, { useEffect } from 'react';
import { defineMessages, FormattedMessage } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { expVal } from '@confluence/feature-experiments';
import { ConfluenceEdition } from '@confluence/session-data/entry-points/SiteInformationQuery.ts';

const messages = defineMessages({
	upgradeButtonText: {
		id: 'experiment-change-edition-upgrade-button-content.upgrade-button.text',
		defaultMessage: 'Upgrade',
		description: 'Text for the upgrade button in the change upgrade button content experiment',
	},
	getStartedButtonText: {
		id: 'experiment-change-edition-upgrade-button-content.get-started-button.text',
		defaultMessage: 'Get started',
		description: 'Text for the get started button in the change upgrade button content experiment',
	},
});

export type ExperimentEligibilityParams = {
	targetEdition?: ConfluenceEdition;
	featureGateId?: string;
};

const isEligibleForExperiment = ({
	targetEdition,
	featureGateId,
}: ExperimentEligibilityParams): boolean => {
	const excludedFeatureGates = [
		'editorAITryPremium',
		'quickSummaryTryPremium',
		'whiteboardPaywallLearnMoreButton',
		'elevateAutomationUpsellExploreAutomation',
		'elevateAutomationUpsellTryPremium',
	];

	if (!featureGateId) {
		return false;
	}

	return (
		targetEdition === ConfluenceEdition.PREMIUM && !excludedFeatureGates.includes(featureGateId)
	);
};

export const useChangeEditionUpgradeButtonContentExperiment = (
	eligibilityParams?: ExperimentEligibilityParams,
): {
	upgradeButtonContent: React.ReactNode | null;
	cohort: string;
} => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const isEligible = eligibilityParams ? isEligibleForExperiment(eligibilityParams) : false;

	const cohort = isEligible
		? expVal('replace_upsell_cta_content', 'cohort', 'not-enrolled')
		: 'not-enrolled';

	useEffect(() => {
		if (cohort === 'not-enrolled') {
			return;
		}

		createAnalyticsEvent({
			type: 'sendTrackEvent',
			data: {
				action: 'exposed',
				actionSubject: 'feature',
				source: 'replace_upsell_cta_content',
				attributes: {
					flagKey: 'replace_upsell_cta_content',
					cohort,
				},
			},
		}).fire();
	}, [cohort, createAnalyticsEvent]);

	if (cohort === 'variant-upgrade') {
		return {
			upgradeButtonContent: <FormattedMessage {...messages.upgradeButtonText} />,
			cohort,
		};
	}

	if (cohort === 'variant-get-started') {
		return {
			upgradeButtonContent: <FormattedMessage {...messages.getStartedButtonText} />,
			cohort,
		};
	}

	return {
		upgradeButtonContent: null,
		cohort,
	};
};
